import { FlowType, flowTypeEnumToString, LanguageCode, OtpType, languageCodeStringToEnum, strToEnum } from "@/enums";
import { loading, replaceState, getTranslations, translation, trackMatomoEvent, isValidMerchantUrl } from "@/utils/common";
import { CheckoutInfo, OtpResponse, TextResourceList } from "@/entities";
import { getTextResources } from "../../../tests/entities";
import { get } from "@/utils/httpRequest";
import { defineComponent } from "vue";
import { otpStore } from "@/store";
import { ModalComponent } from "@/components";
import { useEventOverride } from "@/utils/useEventOverride";

export default defineComponent({
    name: "DateOfBirthView",
    components: {
        ModalComponent,
    },
    props: {
        scaSessionId: {
            type: String,
            required: true,
        },
    },
    setup(_, { emit }) {
        /*
         * workaround for vue's automatic event name transformation that breaks rInput's functionality
         * https://eslint.vuejs.org/rules/custom-event-name-casing
         * > "event listeners inside DOM templates will be automatically transformed to lowercase"
         */
        useEventOverride("rInput", "birthDateInput", "onInput");
    },
    data: function () {
        return {
            data: new CheckoutInfo(),
            redirectUrl: <string | null>"",
            birthDateIsValid: false,
            birthDateHintMessage: "",
            textResourceList: new TextResourceList(),
            OtpType,
            store: otpStore(),
            confirmExitOpen: false,
            birthDate: "",
            selectedOtpType: <OtpType | string>OtpType.Unset,
            minDate: this.getMinDate(),
            maxDate: this.getMaxDate(),
            invalidDateMessage: ""
        };
    },
    async created() {
        replaceState();
        this.data = this.store.otp;
        this.redirectUrl = this.data.redirectUrl;

        loading();
        try {            
            const response = <OtpResponse>await get(`api/verifyotp/init/${this.scaSessionId}`);

            if (response.data) {
                response.data.redirectUrl = this.redirectUrl;
            }

            //redirect to error (or restart) view
            if (!response.isSuccess) {
                this.$router.replace(`/${response.view}/${this.scaSessionId}`);
            }

            //redirect back to merchant
            if (this.redirectUrl && response.returnToMerchant) {
                window.location.replace(this.redirectUrl);
            }

            this.data = response.data ?? new CheckoutInfo();
            this.store.otp = this.data;
            const selectedOtpType = this.data.otpInfo[this.data.otpInfo.length - 1]?.otpType;
            if (selectedOtpType) {
                this.selectedOtpType = strToEnum(<string>selectedOtpType);
            }
        } catch (e) {
            console.log("Error while loading page data.", e);
        } finally {
            loading();
        }
    },
    mounted() {
        this.getData();
        setTimeout(() => {
            const input = document.getElementById("birthDateInput");
            const inputs = input?.shadowRoot?.querySelectorAll(".r-input-date--inputs input:not([type=hidden])");
            
            const placeholder = this.getBirthDatePlaceholder();
            const parts = placeholder.split("/");
            
            inputs?.forEach((i, j) => i.setAttribute("placeholder", parts[j]));

            //set focus to birthDateInput
            input?.click();
        }, 100);
    },
    methods: {
        async getData() {
            loading();

            try {
                this.textResourceList = (await this.getTextResources(this.data.countryCode, languageCodeStringToEnum(this.data.conversationLanguage))) as TextResourceList;
                this.birthDateHintMessage = this.getTranslation("OtpDobHint");
            } catch {
                return;
            } finally {
                loading();
            }
        },
        hasSteps() {
            return this.textResourceList.textResourceItems.length > 0 && (this.data.flowType == flowTypeEnumToString(FlowType.VerifyOtpEmailDob) || this.data.flowType == flowTypeEnumToString(FlowType.VerifyOtpSmsDob));
        },
        getBirthDatePlaceholder() {
            const languageCode = languageCodeStringToEnum(this.data.conversationLanguage);
            switch (languageCode) {
                case LanguageCode.NL:
                    return "DD/MM/JJJJ";
                case LanguageCode.DE:
                    return "TT/MM/JJJJ";
                default:
                    return "DD/MM/YYYY";
            }
        },
        formatDate(date: Date) {
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const dayStr = day < 10 ? `0${day}` : `${day}`;
            const monthStr = month < 10 ? `0${month}` : `${month}`;
            return `${dayStr}/${monthStr}/${year}`;
        },
        getMinDate() {
            const year120 = 1000 * 60 * 60 * 24 * 365 * 120;
            return this.formatDate(new Date(new Date().valueOf() - year120));
        },
        getMaxDate() {
            return this.formatDate(new Date());
        },
        async onInput(event: CustomEvent) {
            this.birthDateIsValid = await (event.target as any)?.checkValidity();

            const value = event.detail.value;
            if (this.birthDateIsValid) {
                const parts = value.split('/');
                this.birthDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
                this.invalidDateMessage = "";
            } else {
                this.birthDate = "";
                this.invalidDateMessage = value.length < 10 
                    ? ""
                    : this.getTranslation('OtpWrongBirthDate');
                
                // forces a rerender with the new invalidDateMessage
                await (event.target as any)?.setCustomValidity();
            }
            
            trackMatomoEvent("Input", "TypesDoB", "OTP-DOB");
        },
        confirmExit() {
            document.body.style.overflow = "hidden";
            this.confirmExitOpen = true;
        },
        closeModal() {
            document.body.style.overflow = "auto";
            this.confirmExitOpen = false;
            window.location.href = window.location.href.split("#")[0];
        },
        async goBack() {
            trackMatomoEvent('Click', 'Go Back', "OTP-DoB");

            //change status to "UserCancel" and redirect to merchant
            const response = <OtpResponse>await get(`api/verifyotp/cancel/${this.scaSessionId}`);
            if (!response.isSuccess) {
                //redirect to error view
                this.$router.replace(`/${response.view}/${this.scaSessionId}`);
            }

            const redirectUrl = response.data?.redirectUrl ?? "https://www.riverty.com";

            
            if (redirectUrl && isValidMerchantUrl(redirectUrl)) {
                window.location.replace(redirectUrl);
            } else {
                // Redirect to error page if merchant URL is invalid
                this.$router.replace(`/error/${this.scaSessionId}`);
            }
        },
        async validateDOB() {
            trackMatomoEvent("Click", "Continue", "OTP-DoB");

            const response = <OtpResponse>await get(`api/verifyotp/verify-birth-date/${this.scaSessionId}/${this.birthDate}`);
            if (response.data) {
                this.data = response.data;
                this.store.otp = response.data;
            }

            //redirect to error view
            if (!response.isSuccess) {
                this.$router.replace(`/${response.view}/${this.scaSessionId}`);
            }

            //redirect back to merchant
            if (this.redirectUrl && response.returnToMerchant) {
                window.location.replace(this.redirectUrl);
            }

            //redirect to next step
            if (response.view) {
                this.$router.replace(`/${response.view}/${this.scaSessionId}`);
            }
        },
        async getTextResources(countryCode: string | null | undefined, languageCode: LanguageCode, agent: unknown | null = null): Promise<TextResourceList | getTextResources> {
            const sysnameList = [
                "OtpBackButton",
                "OtpContinueButton",
                "OtpConfirmDobOnly",
                "OtpConfirmDobOnlyDescription",
                "OtpEnterYourDob",
                "OtpConfirmDob",
                "OtpDobLabel",
                "OtpDobHint",
                "OtpWrongBirthDate",
                "OtpClose",
                "OtpExitVerification",
                "OtpExitVerificationPrompt",
                "OtpExitVerificationDescription",
                "OtpContinueVerification",
                "OtpStart",
                "OtpEmailCode",
                "OtpSmsCode",
                "OtpBirthDateLabel",
            ];

            return await getTranslations(sysnameList, countryCode, languageCode, agent);
        },
        getTranslation(sysName: string, replace: string[] = []): string {
            return translation(this.textResourceList?.textResourceItems, sysName, replace);
        },
    },
});
